import React, { useState, useCallback } from 'react';

// Sort state for a server-sorted table. The keys must match the whitelist the
// API accepts (see utils/sort.js in athens-asset-api) — an unrecognized key
// falls back to the default there rather than erroring.
export function useTableSort(defaultKey, defaultDir = 'asc') {
  const [sort, setSort] = useState(defaultKey);
  const [dir, setDir] = useState(defaultDir);

  // Clicking the active column flips direction; a new column starts ascending.
  const toggle = useCallback(key => {
    if (key === sort) {
      setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir('asc');
    }
  }, [sort]);

  return { sort, dir, toggle };
}

// Clickable column header. Inactive columns keep a dimmed ↕ so it's visible
// which columns can be sorted at all, without competing with the active one.
export default function SortableTh({ label, sortKey, sort, dir, onSort, style }) {
  const active = sort === sortKey;

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSort(sortKey);
    }
  }

  return (
    <th
      onClick={() => onSort(sortKey)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={`Sort by ${label}`}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
    >
      {label}
      {/* Drawn in the header foreground, because the header row is a brand-red
          fill now: the old accent-red-on-red would have been invisible, and the
          dimmed --text-2 close to it. */}
      <span style={{
        marginLeft: 5,
        fontSize: 12,
        color: 'var(--th-fg)',
        opacity: active ? 1 : 0.55,
      }}>
        {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );
}
